import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { 
  ChartBarIcon,
  CurrencyPoundIcon,
  ArrowDownTrayIcon,
  StarIcon,
  EyeIcon,
  PlusIcon,
  EnvelopeIcon,
  FolderIcon,
  TrashIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils';
import { useAuth } from '../context/AuthContext';
import { AddProductModal } from '../components/modals/AddProductModal';
import { productService, Product } from '../services/productService';
import { purchaseService, Purchase } from '../services/purchaseService';
import { favoriteService, Favorite } from '../services/favoriteService';
import { statsService, DashboardStats } from '../services/statsService';
import { sellerOnboardingService, StripeAccountStatus } from '../services/sellerOnboardingService';
import { ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

export const DashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'purchases' | 'sales' | 'favorites' | 'myfiles'>('overview');
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [myProducts, setMyProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [recentPurchases, setRecentPurchases] = useState<Purchase[]>([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [allPurchases, setAllPurchases] = useState<Purchase[]>([]);
  const [isLoadingAllPurchases, setIsLoadingAllPurchases] = useState(false);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [recentSales, setRecentSales] = useState<Purchase[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  const [allSales, setAllSales] = useState<Purchase[]>([]);
  const [isLoadingAllSales, setIsLoadingAllSales] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalPurchases: 0,
    totalSales: 0,
    totalEarnings: 0,
    averageRating: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<StripeAccountStatus | null>(null);
  const [isLoadingStripeStatus, setIsLoadingStripeStatus] = useState(false);
  const { user } = useAuth();

  // Load products when My Files tab is active
  useEffect(() => {
    if (activeTab === 'myfiles' && !isLoadingProducts && myProducts.length === 0) {
      loadMyProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
console.log('testing')
  // Load recent purchases when component mounts or overview tab is active
  useEffect(() => {
    if (activeTab === 'overview' && recentPurchases.length === 0 && !isLoadingPurchases) {
      loadRecentPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load all purchases when purchases tab is active
  useEffect(() => {
    if (activeTab === 'purchases' && allPurchases.length === 0 && !isLoadingAllPurchases) {
      loadAllPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load favorites when favorites tab is active
  useEffect(() => {
    if (activeTab === 'favorites' && favorites.length === 0 && !isLoadingFavorites) {
      loadFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load dashboard stats when component mounts or overview tab is active
  useEffect(() => {
    if (activeTab === 'overview' && !isLoadingStats && stats.totalPurchases === 0 && stats.totalSales === 0) {
      loadDashboardStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load Stripe account status for sellers
  useEffect(() => {
    if (user?.role === 'SELLER' && !isLoadingStripeStatus && !stripeAccountStatus) {
      loadStripeAccountStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab]);

  const loadStripeAccountStatus = async () => {
    setIsLoadingStripeStatus(true);
    try {
      const status = await sellerOnboardingService.getStripeAccountStatus();
      setStripeAccountStatus(status);
    } catch (error) {
      console.error('Error loading Stripe account status:', error);
    } finally {
      setIsLoadingStripeStatus(false);
    }
  };

  // Load recent sales when component mounts or overview tab is active
  useEffect(() => {
    if (activeTab === 'overview' && recentSales.length === 0 && !isLoadingSales) {
      loadRecentSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load all sales when sales tab is active
  useEffect(() => {
    if (activeTab === 'sales' && allSales.length === 0 && !isLoadingAllSales) {
      loadAllSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadRecentPurchases = async () => {
    setIsLoadingPurchases(true);
    try {
      const purchases = await purchaseService.getRecentPurchases();
      setRecentPurchases(purchases);
    } catch (error) {
      console.error('Error loading recent purchases:', error);
      setRecentPurchases([]);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  const loadAllPurchases = async () => {
    setIsLoadingAllPurchases(true);
    try {
      const purchases = await purchaseService.getMyPurchases();
      setAllPurchases(purchases);
    } catch (error) {
      console.error('Error loading purchases:', error);
      setAllPurchases([]);
    } finally {
      setIsLoadingAllPurchases(false);
    }
  };

  const loadMyProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const products = await productService.getMyProducts();
      setMyProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadFavorites = async () => {
    setIsLoadingFavorites(true);
    try {
      const favoritesList = await favoriteService.getMyFavorites();
      setFavorites(favoritesList);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const loadDashboardStats = async () => {
    setIsLoadingStats(true);
    try {
      const dashboardStats = await statsService.getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Keep default stats on error
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadRecentSales = async () => {
    setIsLoadingSales(true);
    try {
      const sales = await purchaseService.getRecentSales();
      console.log('Loaded recent sales:', sales);
      setRecentSales(sales);
    } catch (error: any) {
      console.error('Error loading recent sales:', error);
      console.error('Error details:', error.message, error.response?.data);
      setRecentSales([]);
    } finally {
      setIsLoadingSales(false);
    }
  };

  const loadAllSales = async () => {
    setIsLoadingAllSales(true);
    try {
      const sales = await purchaseService.getMySales();
      console.log('Loaded sales:', sales);
      setAllSales(sales);
    } catch (error: any) {
      console.error('Error loading sales:', error);
      console.error('Error details:', error.message, error.response?.data);
      setAllSales([]);
    } finally {
      setIsLoadingAllSales(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'purchases', name: 'My Purchases', icon: ArrowDownTrayIcon },
    { id: 'sales', name: 'My Sales', icon: CurrencyPoundIcon },
    { id: 'myfiles', name: 'My Files', icon: FolderIcon },
    { id: 'favorites', name: 'Favorites', icon: StarIcon },
  ];

  const getPurchaseImageUrl = (purchase: Purchase): string => {
    if (purchase.product.thumbnailUrl) return purchase.product.thumbnailUrl;
    if (purchase.product.thumbnail) {
      const thumbnailMedia = purchase.product.media?.find(m => m.fileUrl || m.key);
      if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
      if (thumbnailMedia?.key) {
        return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${thumbnailMedia.key}`;
      }
      return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${purchase.product.thumbnail}`;
    }
    return 'https://via.placeholder.com/150x100?text=No+Image';
  };

  const getSaleImageUrl = (sale: Purchase): string => {
    if (sale.product.thumbnailUrl) return sale.product.thumbnailUrl;
    if (sale.product.thumbnail) {
      const thumbnailMedia = sale.product.media?.find(m => m.fileUrl || m.key);
      if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
      if (thumbnailMedia?.key) {
        return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${thumbnailMedia.key}`;
      }
      return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${sale.product.thumbnail}`;
    }
    return 'https://via.placeholder.com/150x100?text=No+Image';
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Seller Onboarding Banner */}
      {user?.role === 'SELLER' && stripeAccountStatus && 
       (!stripeAccountStatus.onboardingCompleted || stripeAccountStatus.status !== 'ENABLED') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Complete Your Payment Setup
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                To receive payments from your product sales, you need to complete your Stripe Connect account setup.
                You'll receive 90% of each sale, with 10% going to the platform.
              </p>
              <div className="mt-3">
                <Link to="/seller/onboarding">
                  <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700 text-white">
                    Complete Setup
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ArrowDownTrayIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Purchases</p>
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-16 bg-secondary-200 rounded mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-secondary-900">{stats.totalPurchases}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CurrencyPoundIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Sales</p>
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-16 bg-secondary-200 rounded mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-secondary-900">{stats.totalSales}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Earnings</p>
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-20 bg-secondary-200 rounded mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-secondary-900">{formatCurrency(stats.totalEarnings)}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <StarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Avg. Rating</p>
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-16 bg-secondary-200 rounded mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-secondary-900">{stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '0.0'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center"
              onClick={() => setIsAddProductModalOpen(true)}
            >
              <PlusIcon className="h-6 w-6 mb-2" />
              Upload New Product
            </Button>
            {/* <Button variant="outline" className="h-20 flex flex-col items-center justify-center">
              <EyeIcon className="h-6 w-6 mb-2" />
              View Analytics
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center">
              <Cog6ToothIcon className="h-6 w-6 mb-2" />
              Account Settings
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Purchases</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingPurchases ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-secondary-600">Loading purchases...</p>
                </div>
              </div>
            ) : recentPurchases.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-secondary-600">No recent purchases</p>
                <Link to="/marketplace" className="text-primary-600 hover:text-primary-700 text-sm mt-2 inline-block">
                  Browse Marketplace →
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPurchases.slice(0, 5).map((purchase) => (
                  <Link
                    key={purchase.id}
                    to={`/product/${purchase.product.id}`}
                    className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors group"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <img
                        src={getPurchaseImageUrl(purchase)}
                        alt={purchase.product.title}
                        className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x100?text=No+Image';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-secondary-900 truncate group-hover:text-primary-600">
                          {purchase.product.title}
                        </h4>
                        <div className="flex items-center space-x-3 text-sm text-secondary-600">
                          <span>{formatCurrency(parseFloat(purchase.product.price))}</span>
                          <span>•</span>
                          <span>{new Date(purchase.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant={purchase.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                      {purchase.paymentStatus === 'PAID' ? 'Completed' : purchase.paymentStatus}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Sales</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingSales ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-secondary-600">Loading sales...</p>
                </div>
              </div>
            ) : recentSales.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-secondary-600">No recent sales</p>
                <p className="text-sm text-secondary-500 mt-1">
                  Sales will appear here when customers purchase your products
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentSales.slice(0, 5).map((sale) => (
                  <Link
                    key={sale.id}
                    to={`/product/${sale.product.id}`}
                    className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors group"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <img
                        src={getSaleImageUrl(sale)}
                        alt={sale.product.title}
                        className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x100?text=No+Image';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-secondary-900 truncate group-hover:text-primary-600">
                          {sale.product.title}
                        </h4>
                        <div className="flex items-center space-x-3 text-sm text-secondary-600">
                          <span>Sold to {sale.buyer.name || sale.buyer.email}</span>
                          <span>•</span>
                          <span>{new Date(sale.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-secondary-900">{formatCurrency(parseFloat(sale.product.price))}</p>
                      <Badge variant="success">Completed</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderPurchases = () => (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-secondary-900">My Purchases</h2>
          <Button variant="outline">
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Download All
          </Button>
        </div>

        {isLoadingAllPurchases ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-secondary-600">Loading purchases...</p>
            </div>
          </div>
        ) : allPurchases.length === 0 ? (
          <div className="text-center py-12">
            <ArrowDownTrayIcon className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              No purchases yet
            </h3>
            <p className="text-secondary-600 mb-6">
              Start browsing the marketplace and purchase your first product
            </p>
            <Link to="/marketplace">
              <Button>
                Browse Marketplace
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {allPurchases.map((purchase) => (
              <Card key={purchase.id} hover>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <img
                        src={getPurchaseImageUrl(purchase)}
                        alt={purchase.product.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x100?text=No+Image';
                        }}
                      />
                      <div className="flex-1">
                        <Link to={`/product/${purchase.product.id}`}>
                          <h3 className="text-lg font-semibold text-secondary-900 mb-2 hover:text-primary-600">
                            {purchase.product.title}
                          </h3>
                        </Link>
                        <div className="flex items-center space-x-4 text-sm text-secondary-600">
                          <span>Purchased: {new Date(purchase.createdAt).toLocaleDateString()}</span>
                          <span>Price: {formatCurrency(parseFloat(purchase.product.price))}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant={purchase.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                        {purchase.paymentStatus === 'PAID' ? 'Completed' : purchase.paymentStatus}
                      </Badge>
                      <Link to={`/product/${purchase.product.id}`}>
                        <Button size="sm">
                          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );

  const renderSales = () => (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-secondary-900">My Sales</h2>
          <Button onClick={() => setIsAddProductModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add New Product
          </Button>
        </div>

        {isLoadingAllSales ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-secondary-600">Loading sales...</p>
            </div>
          </div>
        ) : allSales.length === 0 ? (
          <div className="text-center py-12">
            <CurrencyPoundIcon className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              No sales yet
            </h3>
            <p className="text-secondary-600 mb-6">
              {user?.role === 'SELLER' 
                ? 'Start selling products to see your sales here'
                : 'You need to be a seller to see sales. Switch to seller account to view your sales.'}
            </p>
            {user?.role === 'SELLER' && (
              <Button onClick={() => setIsAddProductModalOpen(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Product
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {allSales.map((sale) => (
              <Card key={sale.id} hover>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <img
                        src={getSaleImageUrl(sale)}
                        alt={sale.product.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x100?text=No+Image';
                        }}
                      />
                      <div className="flex-1">
                        <Link to={`/product/${sale.product.id}`}>
                          <h3 className="text-lg font-semibold text-secondary-900 mb-2 hover:text-primary-600">
                            {sale.product.title}
                          </h3>
                        </Link>
                        <div className="flex items-center space-x-4 text-sm text-secondary-600">
                          <span>Sold: {new Date(sale.createdAt).toLocaleDateString()}</span>
                          <span>Buyer: {sale.buyer.name || sale.buyer.email}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <p className="font-semibold text-secondary-900">{formatCurrency(parseFloat(sale.product.price))}</p>
                        <Badge variant="success">Completed</Badge>
                      </div>
                      <Link to={`/product/${sale.product.id}`}>
                        <Button variant="outline" size="sm">
                          <EyeIcon className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
  );

  const renderMyFiles = () => {
    const handleDelete = async (productId: string) => {
      if (window.confirm('Are you sure you want to delete this product?')) {
        try {
          await productService.deleteProduct(productId);
          setMyProducts(myProducts.filter(p => p.id !== productId));
        } catch (error) {
          console.error('Error deleting product:', error);
          alert('Failed to delete product. Please try again.');
        }
      }
    };

    const getImageUrl = (product: Product): string => {
      // Prefer signed URLs from backend
      if (product.thumbnailUrl) {
        return product.thumbnailUrl;
      }
      if (product.thumbnail) {
        const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
        if (thumbnailMedia?.signedUrl) {
          return thumbnailMedia.signedUrl;
        }
        if (thumbnailMedia?.fileUrl) {
          return thumbnailMedia.fileUrl;
        }
      }
      const firstMedia = product.media?.[0];
      if (firstMedia?.signedUrl) {
        return firstMedia.signedUrl;
      }
      if (firstMedia?.fileUrl) {
        return firstMedia.fileUrl;
      }
      // Default placeholder
      return 'https://via.placeholder.com/300x200?text=No+Image';
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'APPROVED':
          return 'bg-green-100 text-green-800';
        case 'PENDING':
          return 'bg-yellow-100 text-yellow-800';
        case 'REJECTED':
          return 'bg-red-100 text-red-800';
        case 'SUSPENDED':
          return 'bg-gray-100 text-gray-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-secondary-900">My Files</h2>
            <p className="text-sm text-secondary-600 mt-1">
              Manage all your uploaded products and files
            </p>
          </div>
          <Button onClick={() => setIsAddProductModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Upload New Product
          </Button>
        </div>

        {isLoadingProducts ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-secondary-600">Loading your products...</p>
            </div>
          </div>
        ) : myProducts.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              No products yet
            </h3>
            <p className="text-secondary-600 mb-6">
              Start uploading your first product to get started
            </p>
            <Button onClick={() => setIsAddProductModalOpen(true)}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Upload Your First Product
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {myProducts.map((product) => (
              <div
                key={product.id}
                className="group relative bg-white rounded-xl shadow-sm border border-secondary-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                {/* Image Container */}
                <div className="relative aspect-video bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden">
                  <img
                    src={getImageUrl(product)}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image';
                    }}
                  />
                  
                  {/* Status Badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}>
                      {product.status}
                    </span>
                  </div>

                  {/* Actions Menu */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="relative">
                      <button className="p-1.5 bg-white rounded-lg shadow-md hover:bg-secondary-50">
                        <EllipsisVerticalIcon className="h-5 w-5 text-secondary-600" />
                      </button>
                    </div>
                  </div>

                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                      <Link to={`/product/${product.id}`}>
                        <button className="p-2 bg-white rounded-lg shadow-md hover:bg-primary-50 transition-colors">
                          <EyeIcon className="h-5 w-5 text-primary-600" />
                        </button>
                      </Link>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                      >
                        <TrashIcon className="h-5 w-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {product.title}
                  </h3>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-lg font-bold text-primary-600">
                        {formatCurrency(parseFloat(product.price))}
                      </p>
                      <p className="text-xs text-secondary-500">
                        {product.downloads || 0} downloads
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-medium text-secondary-700">
                        {product.rating > 0 ? product.rating.toFixed(1) : '0.0'}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {product.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                      {product.tags.length > 2 && (
                        <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md">
                          +{product.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-3 pt-3 border-t border-secondary-100 flex items-center justify-between text-xs text-secondary-500">
                    <span>
                      {new Date(product.createdAt).toLocaleDateString()}
                    </span>
                    <span>
                      {product.downloads} downloads
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFavorites = () => {
    const getFavoriteImageUrl = (favorite: Favorite): string => {
      if (favorite.product.thumbnailUrl) return favorite.product.thumbnailUrl;
      if (favorite.product.thumbnail) {
        return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${favorite.product.thumbnail}`;
      }
      return 'https://via.placeholder.com/300x200?text=No+Image';
    };

    const handleRemoveFavorite = async (e: React.MouseEvent, productId: string) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (window.confirm('Are you sure you want to remove this product from your favorites?')) {
        try {
          await favoriteService.removeFromFavorites(productId);
          setFavorites(favorites.filter(f => f.product.id !== productId));
        } catch (error) {
          console.error('Error removing favorite:', error);
          alert('Failed to remove favorite. Please try again.');
        }
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-secondary-900">My Favorites</h2>
        </div>

        {isLoadingFavorites ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-secondary-600">Loading favorites...</p>
            </div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <StarIcon className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              No favorites yet
            </h3>
            <p className="text-secondary-600 mb-6">
              Start browsing the marketplace and add products to your favorites
            </p>
            <Link to="/marketplace">
              <Button>
                Browse Marketplace
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((favorite) => (
              <div
                key={favorite.id}
                className="group relative bg-white rounded-xl shadow-sm border border-secondary-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                <Link
                  to={`/product/${favorite.product.id}`}
                  className="block"
                >
                  {/* Image Container */}
                  <div className="relative aspect-video bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden">
                    <img
                      src={getFavoriteImageUrl(favorite)}
                      alt={favorite.product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image';
                      }}
                    />
                    
                    {/* Favorite Badge */}
                    <div className="absolute top-3 left-3">
                      <div className="p-2 bg-white rounded-full shadow-md">
                        <StarIcon className="h-5 w-5 text-yellow-400 fill-current" />
                      </div>
                    </div>

                    {/* Remove Button */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={(e) => handleRemoveFavorite(e, favorite.product.id)}
                        className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                        title="Remove from favorites"
                      >
                        <TrashIcon className="h-5 w-5 text-red-600" />
                      </button>
                    </div>

                    {/* Overlay on Hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="p-2 bg-white rounded-lg shadow-md">
                          <EyeIcon className="h-6 w-6 text-primary-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {favorite.product.title}
                    </h3>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div>
                        <p className="text-lg font-bold text-primary-600">
                          {formatCurrency(parseFloat(favorite.product.price))}
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-3 pt-3 border-t border-secondary-100 text-xs text-secondary-500">
                      <span>
                        Added {new Date(favorite.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-2xl font-bold">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-secondary-900">
                Welcome back, {user.name}!
              </h1>
              <div className="flex items-center space-x-4 mt-1">
                <div className="flex items-center text-secondary-600">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                  <span className="text-sm">{user.email}</span>
                </div>
                <div className="flex items-center">
                  <Badge variant={user.role === 'SELLER' ? 'primary' : 'secondary'}>
                    {user.role === 'SELLER' ? 'Seller' : 'Buyer'} Account
                  </Badge>
                  {user.isEmailVerified && (
                    <Badge variant="success" className="ml-2">
                      Verified
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'purchases' && renderPurchases()}
      {activeTab === 'sales' && renderSales()}
      {activeTab === 'myfiles' && renderMyFiles()}
      {activeTab === 'favorites' && renderFavorites()}

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => setIsAddProductModalOpen(false)}
        onSuccess={() => {
          // Refresh products list
          if (activeTab === 'myfiles') {
            setMyProducts([]); // Reset to trigger reload
          }
          alert('Product created successfully!');
        }}
      />
    </div>
  );
};
